import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    InputAdornment,
    Pagination,
    Snackbar,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {useTranslation} from "react-i18next";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import {attr, Branch, useBranches} from "../../../hooks/useBranches";
import {flatOuterCardSx, flatOuterCardContentSx} from "../../../utils/mobileCardSx";
import MobileStickyActionBar from "../../Generic/mobile/MobileStickyActionBar";
import BranchTableMobile from "./mobile/BranchTableMobile";
// @ts-ignore
import BranchListingTables from "./tables/BranchListingTables";
// @ts-ignore
import AddBranchDialog from "./dialogs/AddBranchDialog";
// @ts-ignore
import EditBranchDialog from "./dialogs/EditBranchDialog";

function StatCard({icon, value, label}: Readonly<{ icon: React.ReactNode; value: number; label: string }>) {
    return (
        <Card variant="outlined" sx={{overflow: "hidden"}}>
            <CardContent sx={{
                display: "flex",
                alignItems: "center",
                gap: {xs: 1, sm: 2},
                p: {xs: 1, sm: 2.5},
                "&:last-child": {pb: {xs: 1, sm: 2.5}},
            }}>
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    width: {xs: 32, sm: 56}, height: {xs: 32, sm: 56}, borderRadius: "50%",
                    bgcolor: (theme) => `${theme.palette.primary.main}1F`, color: "primary.main",
                }}>
                    {icon}
                </Box>
                <Box sx={{minWidth: 0, overflow: "hidden"}}>
                    <Typography variant="h5" fontWeight={700} lineHeight={1.1}>{value}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Branches(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const {
        token,
        loading,
        error,
        setError,
        searchInput,
        setSearchInput,
        searchQuery,
        setSearchQuery,
        selected,
        order,
        orderBy,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        anchorEl,
        menuRowId,
        menuOpen,
        addDialogOpen,
        setAddDialogOpen,
        newCode,
        setNewCode,
        newName,
        setNewName,
        newCity,
        setNewCity,
        newRegion,
        setNewRegion,
        newIsActive,
        setNewIsActive,
        formErrors,
        setFormErrors,
        editDialogOpen,
        editCode,
        setEditCode,
        editName,
        setEditName,
        editCity,
        setEditCity,
        editRegion,
        setEditRegion,
        editIsActive,
        setEditIsActive,
        editFormErrors,
        setEditFormErrors,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        setSuccessMessageText,
        fetchBranches,
        handleRequestSort,
        filteredBranches,
        sortedBranches,
        paginatedBranches,
        handleSelectAll,
        handleRowClick,
        isSelected,
        handleMenuOpen,
        handleMenuClose,
        handleMenuAction,
        handleStatusUpdate,
        handleEditOpen,
        handleEditClose,
        handleEditSave,
        handleCloseDialog,
        validateForm,
    } = useBranches();

    const isApproved = (b: Branch) => attr(b, "status", b.status)?.toUpperCase() === "APPROVED";
    const activeCount = filteredBranches.filter((b) => attr(b, "is_active", String(b.isActive)).toUpperCase() === "TRUE").length;
    const approvedCount = filteredBranches.filter(isApproved).length;
    const pendingCount = filteredBranches.filter((b) => !["APPROVED", "REJECTED"].includes(attr(b, "status", b.status)?.toUpperCase())).length;

    return (
        <Box sx={{width: "100%", textAlign: "left", display: "flex", flexDirection: "column", gap: 2}}>
            {/* Page header: title lives here only (BranchesPage renders no breadcrumb). */}
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2}}>
                <Box sx={{minWidth: 0, textAlign: "left"}}>
                    <Typography variant="h5" component="h1" fontWeight={700}
                                sx={{textAlign: "left"}}>
                        {t("hr.branches.page.title")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{textAlign: "left"}}>
                        {t("hr.branches.page.subtitle")}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setAddDialogOpen(true)}
                        sx={{flexShrink: 0, whiteSpace: "nowrap"}}>
                    {isMobile ? t("hr.branches.addBranchShort") : t("hr.branches.addBranch")}
                </Button>
            </Box>

            <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: {xs: 1, sm: 2}}}>
                <StatCard icon={<PlaceOutlinedIcon/>} value={activeCount} label={t("hr.branches.stats.active")}/>
                <StatCard icon={<CheckCircleOutlineIcon/>} value={approvedCount} label={t("hr.branches.stats.approved")}/>
                <StatCard icon={<HourglassEmptyIcon/>} value={pendingCount} label={t("hr.branches.stats.pending")}/>
            </Box>

            <Card variant="outlined" sx={isMobile ? flatOuterCardSx(true) : {border: "1px solid", borderColor: "divider"}}>
                <CardContent sx={flatOuterCardContentSx(isMobile)}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{textAlign: "left", mb: 2}}>
                        {t('hr.branches.management.title')}
                    </Typography>

                    {/* Error */}
                    {error && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}

                    {/* Search — inside card, top left, 50% width */}
                    <Box sx={{mb: 2, display: "flex", justifyContent: "flex-start"}}>
                        <TextField
                            placeholder={t('hr.branches.search.placeholder')}
                            variant="outlined"
                            size="small"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setSearchQuery(searchInput);
                                    setCurrentPage(1);
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action"/>
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchInput("");
                                                setSearchQuery("");
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <ClearIcon fontSize="small"/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{width: {xs: "100%", sm: "100%"}}}
                        />
                    </Box>
                    <br/>

                    {isMobile ? (
                        <BranchTableMobile
                            branches={sortedBranches}
                            loading={loading}
                            error={error}
                            searchQuery={searchQuery}
                            selected={selected}
                            page={currentPage}
                            rowsPerPage={itemsPerPage}
                            isSelected={isSelected}
                            handleRowClick={handleRowClick}
                            handleApprove={(id) => handleStatusUpdate([id], "APPROVED")}
                            handleEdit={handleEditOpen}
                            handleReject={(id) => handleStatusUpdate([id], "REJECTED")}
                        />
                    ) : (
                        <BranchListingTables
                            loading={loading}
                            sortedBranches={sortedBranches}
                            paginatedBranches={paginatedBranches}
                            searchQuery={searchQuery}
                            order={order}
                            orderBy={orderBy}
                            handleRequestSort={handleRequestSort}
                            selected={selected}
                            filteredBranches={filteredBranches}
                            handleSelectAll={handleSelectAll}
                            handleRowClick={handleRowClick}
                            isSelected={isSelected}
                            anchorEl={anchorEl}
                            menuOpen={menuOpen}
                            menuRowId={menuRowId}
                            handleMenuOpen={handleMenuOpen}
                            handleMenuClose={handleMenuClose}
                            handleMenuAction={handleMenuAction}
                            handleStatusUpdate={handleStatusUpdate}
                            handleEditOpen={handleEditOpen}
                        />
                    )}

                    {/* Pagination — always visible */}
                    <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                        <Pagination
                            count={Math.max(1, Math.ceil(sortedBranches.length / itemsPerPage))}
                            color="primary"
                            page={currentPage}
                            onChange={(_e, page) => setCurrentPage(page)}
                            disabled={loading || sortedBranches.length === 0}
                        />
                    </Box>

                    {/* Bottom action bar — inside card: bulk Approve/Reject (Add Branch is in the page header) */}
                    {isMobile ? (
                        <MobileStickyActionBar>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleOutlineIcon/>}
                                disabled={selected.length === 0}
                                onClick={() => handleStatusUpdate(selected, "APPROVED")}
                            >
                                {t('common.actions.approve')}
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                startIcon={<CancelOutlinedIcon/>}
                                disabled={selected.length === 0}
                                onClick={() => handleStatusUpdate(selected, "REJECTED")}
                            >
                                {t('common.actions.reject')}
                            </Button>
                        </MobileStickyActionBar>
                    ) : (
                    <Box sx={{display: "flex", justifyContent: "flex-start", alignItems: "center", mt: 3}}>
                            <Box sx={{display: "flex", gap: 1}}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleOutlineIcon/>}
                                    disabled={selected.length === 0}
                                    onClick={() => handleStatusUpdate(selected, "APPROVED")}
                                >
                                    {t('common.actions.approve')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CancelOutlinedIcon/>}
                                    disabled={selected.length === 0}
                                    onClick={() => handleStatusUpdate(selected, "REJECTED")}
                                >
                                    {t('common.actions.reject')}
                                </Button>
                            </Box>
                        </Box>
                        )}

                </CardContent>
            </Card>

            {/* Add Branch dialog — disableEscapeKeyDown prevents Esc close; no onClose on backdrop */}
            <AddBranchDialog
                open={addDialogOpen}
                token={token}
                newCode={newCode}
                setNewCode={setNewCode}
                newName={newName}
                setNewName={setNewName}
                newCity={newCity}
                setNewCity={setNewCity}
                newRegion={newRegion}
                setNewRegion={setNewRegion}
                newIsActive={newIsActive}
                setNewIsActive={setNewIsActive}
                formErrors={formErrors}
                setFormErrors={setFormErrors}
                validateForm={validateForm}
                setError={setError}
                setSuccessMessageText={setSuccessMessageText}
                setShowSuccessMessage={setShowSuccessMessage}
                fetchBranches={fetchBranches}
                onClose={handleCloseDialog}
            />

            {/* Edit Branch dialog */}
            <EditBranchDialog
                open={editDialogOpen}
                editCode={editCode}
                setEditCode={setEditCode}
                editName={editName}
                setEditName={setEditName}
                editCity={editCity}
                setEditCity={setEditCity}
                editRegion={editRegion}
                setEditRegion={setEditRegion}
                editIsActive={editIsActive}
                setEditIsActive={setEditIsActive}
                editFormErrors={editFormErrors}
                setEditFormErrors={setEditFormErrors}
                onClose={handleEditClose}
                onSave={handleEditSave}
            />

            {/* Success Snackbar */} <Snackbar
            open={showSuccessMessage}
            autoHideDuration={3000}
            onClose={() => setShowSuccessMessage(false)}
            anchorOrigin={{vertical: "top", horizontal: "right"}}
        >
            <Alert
                severity="success"
                onClose={() => setShowSuccessMessage(false)}
                sx={{width: "100%"}}
            >
                {successMessageText}
            </Alert>
        </Snackbar>
        </Box>
    );
}
